import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { SettingState } from "../../types";
import { AppConfigs, PushNotification, RegularMarketHours, ServiceAvailability, ServiceAvailabilityKey, SpecialMarketHours } from "../../../utils/types";
import { axiosObj } from "../../../config/apiClients";

const initialState: SettingState = {
  isLoading: false,
  settings: [],
  marketHours: null,
  appconfigs: null,
  serviceAvailability: null,
}

export const createPushNotification = createAsyncThunk(
  "/settings/broadcastPushNotification",
  async (notification: PushNotification, { rejectWithValue }) => {
    try {
      const response = await axiosObj.post('/settings/broadcastPushNotification', notification);
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to Broad Cast Push Notification' });
    }
  }
)

export const broadcastAppVersion = createAsyncThunk(
  "/settings/update-version",
  async (version: AppConfigs, { rejectWithValue }) => {
    try {
      const response = await axiosObj.patch('/settings/update-version', version);
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to Broad App Version' });
    }
  }
)

export const getAppConfigs = createAsyncThunk(
  "/settings/app-configs",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosObj.get('/settings/app-version');
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to fetch App Configs' });
    }
  }
)

export const updateMaintenanceMode = createAsyncThunk(
  "/settings/update-maintenanceMode",
  async (isMaintenanceMode: boolean, { rejectWithValue }) => {
    try {
      const response = await axiosObj.patch('/settings/maintenance-mode', { isMaintenanceMode });
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to update Maintenance Mode' });
    }
  }
)

export const getServiceAvailability = createAsyncThunk(
  "/service-availability/get",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosObj.get(
        "/settings/service-availability"
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error || {
          message: "Failed to fetch service availability",
        }
      );
    }
  }
);

export const updateServiceAvailability = createAsyncThunk(
  "/settings/service-availability/update",
  async (
    payload: Partial<ServiceAvailability>,
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosObj.patch(
        "/settings/service-availability/update",
        payload
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error || {
          message: "Failed to update service availability",
        }
      );
    }
  }
);

export const getMarketHours = createAsyncThunk(
  "settings/getMarketHours",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosObj.get("/settings/market-hours");
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data ?? err.message);
    }
  }
);

export const updateRegularMarketHours = createAsyncThunk(
  "settings/updateMarketHours",
  async (body: RegularMarketHours, { rejectWithValue }) => {
    try {
      const response = await axiosObj.patch("/settings/market-hours", body);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data ?? err.message);
    }
  }
);

export const updateSpecialHours = createAsyncThunk(
  "settings/updateSpecialHours",
  async (body: SpecialMarketHours, { rejectWithValue }) => {
    try {
      const response = await axiosObj.patch("/settings/market-hours/special", body);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data ?? err.message);
    }
  }
);

const settingsSlice = createSlice({
  name: "settings",
  initialState,
  reducers: {
    toggleServiceAvailability: (state, action: PayloadAction<{
      service: ServiceAvailabilityKey;
      enabled: boolean;
    }>) => {
      const { service, enabled } = action.payload;

      if (state.serviceAvailability) {
        state.serviceAvailability[service] = enabled;
      }
    },
    updateAppVersion: (state, action) => {
      state.appconfigs = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getMarketHours.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getMarketHours.fulfilled, (state, action) => {
        state.isLoading = false;
        state.marketHours = action.payload.payload;
      })
      .addCase(getMarketHours.rejected, (state) => {
        state.isLoading = false;
        state.marketHours = null;
      })
      .addCase(createPushNotification.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createPushNotification.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(createPushNotification.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(broadcastAppVersion.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(broadcastAppVersion.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(broadcastAppVersion.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(getAppConfigs.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getAppConfigs.fulfilled, (state, action) => {
        state.isLoading = false;
        state.appconfigs = action.payload.payload;
      })
      .addCase(getAppConfigs.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(updateMaintenanceMode.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateMaintenanceMode.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(updateMaintenanceMode.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(getServiceAvailability.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getServiceAvailability.fulfilled, (state, action) => {
        state.isLoading = false;
        state.serviceAvailability = action.payload.payload;
      })
      .addCase(getServiceAvailability.rejected, (state) => {
        state.isLoading = false;
      })

      .addCase(updateServiceAvailability.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateServiceAvailability.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(updateServiceAvailability.rejected, (state) => {
        state.isLoading = false;
      });
  }
});

export const { toggleServiceAvailability, updateAppVersion } = settingsSlice.actions;

export default settingsSlice.reducer;